import { memo } from 'react';
import styled from 'styled-components';
import { IconCheck, IconHelpCircle } from '@u21/tabler-icons';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import {
  U21AiChatBubbleInnerProps,
  U21AiChatBubbleType,
} from 'app/shared/u21-ui/components/input/chat/shared';
import { U21AiChatButtonInner } from 'app/shared/u21-ui/components/input/chat/U21AiChatButtonInner';
import { U21AiChatMultiSelectInner } from 'app/shared/u21-ui/components/input/chat/U21AiChatMultiSelectInner';
import { U21AiChatSingleSelectInner } from 'app/shared/u21-ui/components/input/chat/U21AiChatSingleSelectInner';

type U21AiChatBubbleButtonProps = U21AiChatBubbleInnerProps & {
  type: U21AiChatBubbleType.BUTTON;
  values: string;
  onSubmit: (answer: string) => void;
};

type U21AiChatBubbleMultiSelectProps = U21AiChatBubbleInnerProps & {
  type: U21AiChatBubbleType.MULTISELECT;
  values: string[];
  onSubmit: (answer: string[]) => void;
};

type U21AiChatBubbleSingleSelectProps = U21AiChatBubbleInnerProps & {
  type: U21AiChatBubbleType.SINGLESELECT;
  values: string;
  onSubmit: (answer: string) => void;
};

export type U21AiChatBubbleProps = {
  question: string;
  readonly?: boolean;
} & (
  | U21AiChatBubbleButtonProps
  | U21AiChatBubbleMultiSelectProps
  | U21AiChatBubbleSingleSelectProps
);

type RenderInnerProps = {
  disabled?: boolean;
  choices: string[];
} & (
  | {
      type: U21AiChatBubbleType.BUTTON;
      onSubmit: (answer: string) => void;
    }
  | {
      type: U21AiChatBubbleType.MULTISELECT;
      initialValues: string[];
      onSubmit: (answer: string[]) => void;
    }
  | {
      type: U21AiChatBubbleType.SINGLESELECT;
      initialValues: string;
      onSubmit: (answer: string) => void;
    }
);

const renderInner = (props: RenderInnerProps) => {
  switch (props.type) {
    case U21AiChatBubbleType.BUTTON:
      return (
        <U21AiChatButtonInner
          choices={props.choices}
          disabled={props.disabled}
          onSubmit={props.onSubmit}
        />
      );
    case U21AiChatBubbleType.MULTISELECT:
      return (
        <U21AiChatMultiSelectInner
          choices={props.choices}
          disabled={props.disabled}
          initialValues={props.initialValues}
          onSubmit={props.onSubmit}
        />
      );
    case U21AiChatBubbleType.SINGLESELECT:
      return (
        <U21AiChatSingleSelectInner
          choices={props.choices}
          disabled={props.disabled}
          initialValues={props.initialValues}
          onSubmit={props.onSubmit}
        />
      );
    default:
      return null;
  }
};

export const U21AiChatBubble = memo(
  ({
    type,
    question,
    choices,
    disabled,
    readonly,
    values,
    onSubmit,
  }: U21AiChatBubbleProps) => {
    if (readonly) {
      const selectedValues = [values]
        .flat()
        .filter((v): v is string => Boolean(v));
      return (
        <BubbleCard>
          <U21Spacer spacing={1.5}>
            <U21Spacer horizontal align="start">
              <StyledIconHelpCircle size={18} $greyed />
              <U21Typography variant="subtitle2" color="text.secondary">
                {question}
              </U21Typography>
            </U21Spacer>

            {selectedValues.length > 0 && (
              <U21Spacer horizontal align="start">
                <StyledIconCheck />
                <U21Typography variant="subtitle2">
                  {new Intl.ListFormat('en', {
                    style: 'long',
                    type: 'conjunction',
                  }).format(selectedValues)}
                </U21Typography>
              </U21Spacer>
            )}
          </U21Spacer>
        </BubbleCard>
      );
    }

    return (
      <BubbleCard>
        <U21Spacer spacing={1.5}>
          <U21Spacer horizontal align="start">
            <StyledIconHelpCircle size={18} />
            <U21Typography variant="subtitle2">{question}</U21Typography>
          </U21Spacer>

          {type === U21AiChatBubbleType.BUTTON &&
            renderInner({ type, choices, disabled, onSubmit })}
          {type === U21AiChatBubbleType.MULTISELECT &&
            renderInner({
              type,
              choices,
              disabled,
              initialValues: values,
              onSubmit,
            })}
          {type === U21AiChatBubbleType.SINGLESELECT &&
            renderInner({
              type,
              choices,
              disabled,
              initialValues: values,
              onSubmit,
            })}
        </U21Spacer>
      </BubbleCard>
    );
  },
);

const BubbleCard = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-left: 4px solid ${({ theme }) => theme.palette.primary.main};
  padding: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 0 ${({ theme }) => theme.shape.borderRadius}px
    ${({ theme }) => theme.shape.borderRadius}px 0;
`;

const StyledIconHelpCircle = styled(IconHelpCircle)<{ $greyed?: boolean }>`
  color: ${({ theme, $greyed }) =>
    $greyed ? theme.palette.text.secondary : theme.palette.info.main};
  flex-shrink: 0;
`;

const StyledIconCheck = styled(IconCheck)`
  color: ${({ theme }) => theme.palette.primary.main};
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  padding-top: ${({ theme }) => theme.spacing(0.5)};
`;
